import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { usePermissions } from '../../hooks/usePermissions';
import { useNavigate } from 'react-router-dom';
import { getVehicles } from '../../services/firestore/vehicleService';
import { getReservationsByDateRange } from '../../services/business/reservationService';
import { isTransporterMatch, buildUserIdentities, buildTransporterIdentities } from '../../services/business/transporterMatchingService';
import { getAllTransporters } from '../../services/firestore/transporterService';
import { getCarrierPerformanceStats } from '../../services/business/carrierPerformanceService';
import { motion, AnimatePresence } from 'framer-motion';
import {
    LayoutDashboard,
    Truck,
    Calendar,
    CheckCircle,
    Clock,
    PlusCircle,
    List,
    Headphones,
    ArrowUpRight,
    TrendingUp,
    Bell,
    Star,
    Target,
    BarChart3,
    ShieldCheck,
    AlertCircle
} from 'lucide-react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import '../../styles/components/transporterDashboard.css';

const Dashboard = ({ userData }) => {
    const { user } = usePermissions(userData);
    const { theme } = useTheme();
    const navigate = useNavigate();
    const isDark = theme !== 'light';

    const [stats, setStats] = useState({
        pending: 0,
        confirmed: 0,
        today: 0,
        activeVehicles: 0,
        chartData: []
    });
    const [performance, setPerformance] = useState(null);
    const [loading, setLoading] = useState(true);

    const transporterId = useMemo(() => {
        return userData?.transporterId || userData?.scope?.transporterId ||
            user?.transporterId || user?.scope?.transporterId;
    }, [userData, user]);

    useEffect(() => {
        if (transporterId && user?.tenantId) {
            loadStats();
        }
    }, [transporterId, user?.tenantId]);

    const loadStats = async () => {
        setLoading(true);
        const tid = transporterId?.toString().trim();
        console.log('[Dashboard] Loading stats for:', {
            tenantId: user?.tenantId,
            transporterId: tid,
            fullUser: user
        });

        try {
            const myIdentities = buildUserIdentities(userData);
            console.log('[Dashboard] Resolved identities:', Array.from(myIdentities));

            // Enrich identities from master list
            try {
                const tData = await getAllTransporters(user.tenantId);
                tData?.forEach(t => {
                    const tIdentities = buildTransporterIdentities(t);
                    if (Array.from(tIdentities).some(id => myIdentities.has(id))) {
                        tIdentities.forEach(id => myIdentities.add(id));
                    }
                });
            } catch (err) {
                console.warn('[Dashboard] Identity enrichment failed:', err);
            }

            const now = new Date();
            const startLimit = new Date();
            startLimit.setDate(now.getDate() - 7);

            const [reservationsData, vehicles, perfData] = await Promise.all([
                getReservationsByDateRange(user.tenantId, startLimit.toISOString().split('T')[0], now.toISOString().split('T')[0]),
                getVehicles({ tenantId: user.tenantId, transporterId: tid }),
                getCarrierPerformanceStats(user.tenantId, {
                    transporterId: tid,
                    startDate: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
                    endDate: dayjs().format('YYYY-MM-DD')
                })
            ]);

            console.log('[Dashboard] Raw data fetched:', {
                reservationsCount: reservationsData.length,
                vehiclesCount: vehicles.length,
                performance: perfData?.carriers?.[0]?.metrics
            });

            if (perfData?.carriers?.length > 0) {
                setPerformance(perfData.carriers[0].metrics);
            }

            const toDate = (val) => {
                if (!val) return null;
                if (val.toDate) return val.toDate();
                const d = new Date(val);
                return isNaN(d.getTime()) ? null : d;
            };

            const myReservations = reservationsData.filter(r => isTransporterMatch(r, myIdentities));

            // Today's range
            const startOfDay = new Date();
            startOfDay.setHours(0, 0, 0, 0);
            const endOfDay = new Date();
            endOfDay.setHours(23, 59, 59, 999);

            // Current Stats
            const pending = myReservations.filter(r => ['pending', 'pending_transporter_confirmation', 'pending_approval'].includes(r.status)).length;
            const confirmed = myReservations.filter(r => ['confirmed', 'approved'].includes(r.status)).length;
            const todayCount = myReservations.filter(r => {
                const d = toDate(r.startTime);
                return d && d >= startOfDay && d <= endOfDay;
            }).length;

            // Chart Data Construction
            const days = [];
            for (let i = 6; i >= 0; i--) {
                const date = new Date(now);
                date.setDate(now.getDate() - i);

                // Construct YYYY-MM-DD in local time
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const dateStr = `${year}-${month}-${day}`;

                const count = myReservations.filter(r => {
                    const rd = toDate(r.startTime);
                    if (!rd) return false;
                    const rYear = rd.getFullYear();
                    const rMonth = String(rd.getMonth() + 1).padStart(2, '0');
                    const rDay = String(rd.getDate()).padStart(2, '0');
                    return `${rYear}-${rMonth}-${rDay}` === dateStr;
                }).length;

                days.push({
                    name: date.toLocaleDateString('tr-TR', { weekday: 'short' }),
                    value: count
                });
            }

            setStats({
                pending,
                confirmed,
                today: todayCount,
                activeVehicles: vehicles.length,
                chartData: days
            });
        } catch (err) {
            console.error('[Dashboard] loadStats failed:', err);
        } finally {
            setLoading(false);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: 'spring', stiffness: 100 }
        }
    };

    const StatCard = ({ title, value, icon: Icon, color, subtext, onClick }) => (
        <motion.div
            variants={itemVariants}
            className={`modern-stat-card glass-panel glow-${color}`}
            onClick={onClick}
        >
            <div className="icon-wrapper">
                <Icon size={24} color="#fff" />
            </div>
            <div className="stat-info">
                <div className="label">{title}</div>
                <div className="value">
                    {loading ? (
                        <span className="opacity-20">--</span>
                    ) : value}
                </div>
                <div className="text-secondary text-xs flex items-center mt-1">
                    {subtext}
                </div>
            </div>
        </motion.div>
    );

    if (!user?.transporterId && !loading) {
        return <div className="p-xl text-center glass-panel m-xl">Lütfen nakliyeci profilinizin bağlandığından emin olun.</div>;
    }

    return (
        <div className="transporter-dashboard container">
            <AnimatePresence>
                <motion.div
                    key="dashboard-content"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    {/* Hero Section */}
                    <motion.div variants={itemVariants} className="welcome-hero glass-panel led-glow">
                        <div className="hero-content flex flex-col items-center">
                            <h1 className="hero-title">
                                Hoş Geldiniz, {user.companyName || 'Değerli İş Ortağımız'}
                            </h1>
                            <p className="hero-subtitle">
                                Operasyonel durumunuzu takip edin ve rezervasyonlarınızı yönetin.
                            </p>
                            <div className="hero-badges">
                                <div className="performance-badge primary">
                                    <TrendingUp size={18} />
                                    <span>Puan Durumu: <span className="font-bold text-lg ml-1">%{performance ? Math.round(performance.score * 10) : '94'}</span></span>
                                </div>
                                <div className="performance-badge success">
                                    <CheckCircle size={18} />
                                    <span>Aktif Filo: <span className="font-bold text-lg ml-1">{stats.activeVehicles} Araç</span></span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Stats Grid */}
                    <div className="stats-grid">
                        <StatCard
                            title="Onay Bekleyen"
                            value={stats.pending}
                            icon={Clock}
                            color="warning"
                            subtext="İşlem gerektirenler"
                            onClick={() => navigate('/transporter/reservations')}
                        />
                        <StatCard
                            title="Bugünkü Yükler"
                            value={stats.today}
                            icon={Calendar}
                            color="primary"
                            subtext="Planlanan operasyonlar"
                            onClick={() => navigate('/transporter/reservations')}
                        />
                        <StatCard
                            title="Onaylanmış"
                            value={stats.confirmed}
                            icon={CheckCircle}
                            color="success"
                            subtext="Hazır rezervasyonlar"
                            onClick={() => navigate('/transporter/reservations')}
                        />
                        <StatCard
                            title="Araç Sayısı"
                            value={stats.activeVehicles}
                            icon={Truck}
                            color="info"
                            subtext="Kayıtlı araç listesi"
                            onClick={() => navigate('/transporter/vehicles')}
                        />
                    </div>

                    {/* Performance Metrics - SEPARATE CARDS GRID */}
                    {performance && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-lg mb-xl mt-10">
                            {/* Metric 1: On-Time */}
                            <motion.div
                                variants={itemVariants}
                                className="glass-panel p-8 led-glow flex flex-col items-center hover:bg-white/[0.04] transition-all duration-300"
                                style={{}}
                            >
                                <div style={{ width: 160, height: 160 }} className="relative flex items-center justify-center mt-4">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={[
                                                    { value: Math.max(1, Math.round(performance.onTimeRate)) },
                                                    { value: 100 - Math.max(1, Math.round(performance.onTimeRate)) }
                                                ]}
                                                cx="50%"
                                                cy="60%"
                                                innerRadius={55}
                                                outerRadius={75}
                                                startAngle={210}
                                                endAngle={-30}
                                                paddingAngle={0}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                <Cell fill="#f59e0b" />
                                                <Cell fill="rgba(255,255,255,0.05)" />
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pt-8">
                                        <span className="text-secondary text-[9px] uppercase tracking-widest font-black opacity-40 mb-1">ZAMANINDA</span>
                                        <span className="text-3xl font-black text-warning leading-none">%{Math.round(performance.onTimeRate)}</span>
                                    </div>
                                </div>
                                <p className="text-[11px] text-secondary opacity-50 text-center mt-3 leading-relaxed">Planlanan saate göre rampa giriş performansı</p>
                            </motion.div>

                            {/* Metric 2: Accuracy */}
                            <motion.div
                                variants={itemVariants}
                                className="glass-panel p-8 led-glow flex flex-col items-center hover:bg-white/[0.04] transition-all duration-300"
                                style={{}}
                            >
                                <div style={{ width: 160, height: 160 }} className="relative flex items-center justify-center mt-4">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={[
                                                    { value: Math.max(1, Math.round((performance.plateAccuracy + performance.driverAccuracy) / 2)) },
                                                    { value: 100 - Math.max(1, Math.round((performance.plateAccuracy + performance.driverAccuracy) / 2)) }
                                                ]}
                                                cx="50%"
                                                cy="60%"
                                                innerRadius={55}
                                                outerRadius={75}
                                                startAngle={210}
                                                endAngle={-30}
                                                paddingAngle={0}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                <Cell fill="#10b981" />
                                                <Cell fill="rgba(255,255,255,0.05)" />
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pt-8">
                                        <span className="text-secondary text-[9px] uppercase tracking-widest font-black opacity-40 mb-1">DOĞRULUK</span>
                                        <span className="text-3xl font-black text-success leading-none">%{Math.round((performance.plateAccuracy + performance.driverAccuracy) / 2)}</span>
                                    </div>
                                </div>
                                <p className="text-[11px] text-secondary opacity-50 text-center mt-3 leading-relaxed">Plaka ve sürücü bilgilerinin veri tutarlılığı</p>
                            </motion.div>

                            {/* Metric 3: Efficiency */}
                            <motion.div
                                variants={itemVariants}
                                className="glass-panel p-8 led-glow flex flex-col items-center hover:bg-white/[0.04] transition-all duration-300"
                                style={{}}
                            >
                                <div style={{ width: 160, height: 160 }} className="relative flex items-center justify-center mt-4">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={[
                                                    { value: Math.max(1, Math.round(performance.volumeOccupancyRate)) },
                                                    { value: 100 - Math.max(1, Math.round(performance.volumeOccupancyRate)) }
                                                ]}
                                                cx="50%"
                                                cy="60%"
                                                innerRadius={55}
                                                outerRadius={75}
                                                startAngle={210}
                                                endAngle={-30}
                                                paddingAngle={0}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                <Cell fill="#3b82f6" />
                                                <Cell fill="rgba(255,255,255,0.05)" />
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pt-8">
                                        <span className="text-secondary text-[9px] uppercase tracking-widest font-black opacity-40 mb-1">HACİM</span>
                                        <span className="text-3xl font-black text-info leading-none">%{Math.round(performance.volumeOccupancyRate)}</span>
                                    </div>
                                </div>
                                <p className="text-[11px] text-secondary opacity-50 text-center mt-3 leading-relaxed">Araç doluluk ve operasyonel verimlilik oranı</p>
                            </motion.div>
                        </div>
                    )}

                    {/* Main Content Area */}
                    <div className="quick-actions-section mb-xl">
                        {/* Activity Chart */}
                        <motion.div
                            variants={itemVariants}
                            className="glass-panel p-8 led-glow"
                            style={{}}
                        >
                            <div className="flex flex-col items-center text-center mb-8 pt-20">
                                <h3 className="font-bold flex items-center gap-2 mb-2">
                                    <TrendingUp size={20} className="text-primary" />
                                    Haftalık Aktivite
                                </h3>
                                <p className="text-xs text-secondary">Son 7 günlük rezervasyon yoğunluğu</p>
                            </div>

                            <div style={{ width: '100%', height: 240 }} className="mt-4">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={stats.chartData}>
                                        <defs>
                                            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="var(--primary-color)" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="var(--primary-color)" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"} />
                                        <XAxis
                                            dataKey="name"
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: 'var(--text-secondary)', fontSize: 12, fontWeight: 500 }}
                                        />
                                        <YAxis hide />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: 'rgba(26, 26, 26, 0.95)',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                borderRadius: '16px',
                                                boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
                                                backdropFilter: 'blur(10px)'
                                            }}
                                            itemStyle={{ color: 'var(--primary-color)' }}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="value"
                                            stroke="var(--primary-color)"
                                            fillOpacity={1}
                                            fill="url(#colorValue)"
                                            strokeWidth={3}
                                            animationDuration={2000}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </motion.div>

                        {/* Announcements Card */}
                        <motion.div
                            variants={itemVariants}
                            className="glass-panel p-8 led-glow flex flex-col"
                            style={{}}
                        >
                            <h3 className="font-bold mb-8 pt-20 flex items-center justify-center gap-2">
                                <Bell size={20} className="text-primary" />
                                Duyurular
                            </h3>
                            <div className="flex-1 flex flex-col items-center justify-center text-center py-10">
                                <motion.div
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ duration: 0.5 }}
                                    className="text-5xl mb-6 opacity-20 filter grayscale"
                                >
                                    📢
                                </motion.div>
                                <p className="text-secondary font-medium">Henüz yeni bir duyuru bulunmamaktadır.</p>
                                <p className="text-xs text-secondary opacity-50 mt-3 max-w-[200px] leading-relaxed">
                                    Sistem güncellemeleri ve önemli bilgilendirmeler burada görüntülenecektir.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default Dashboard;

